import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import mammoth from 'mammoth';

const PDF_MIME = 'application/pdf';
const DOCX_MIME =
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function isPdf(mimeType) {
	return (mimeType || '').toLowerCase() === PDF_MIME;
}

function isDocx(mimeType) {
	return (mimeType || '').toLowerCase() === DOCX_MIME;
}

function FileViewerState({ children }) {
	return (
		<div className="file-viewer-state">
			<img src="/ats/icons/document-icon.svg" alt="" />
			{children}
		</div>
	);
}

export default function FileViewer({
	src,
	mimeType,
	fileName,
	loading,
	error,
	onRetry
}) {
	const [docxHtml, setDocxHtml] = useState(null);
	const [docxError, setDocxError] = useState(false);

	useEffect(() => {
		if (!src || !isDocx(mimeType)) {
			setDocxHtml(null);
			setDocxError(false);
			return undefined;
		}

		let cancelled = false;
		setDocxHtml(null);
		setDocxError(false);

		const convert = async () => {
			try {
				const response = await fetch(src);
				const arrayBuffer = await response.arrayBuffer();
				const result = await mammoth.convertToHtml({ arrayBuffer });
				if (cancelled) return;
				setDocxHtml(DOMPurify.sanitize(result.value));
			} catch (convertError) {
				if (!cancelled) {
					setDocxError(true);
				}
			}
		};

		convert();

		return () => {
			cancelled = true;
		};
	}, [src, mimeType]);

	if (loading) {
		return (
			<div className="FileViewer is-loading">
				<div className="file-viewer-state">Loading resume...</div>
			</div>
		);
	}

	if (error === 'missing') {
		return (
			<div className="FileViewer is-empty">
				<FileViewerState>
					<p>No resume was uploaded with this application.</p>
				</FileViewerState>
			</div>
		);
	}

	if (error === 'failed' || docxError) {
		return (
			<div className="FileViewer is-error">
				<FileViewerState>
					<p>The resume could not be loaded.</p>
					{onRetry ? (
						<button type="button" className="ats-button-soft" onClick={onRetry}>
							Try again
						</button>
					) : null}
				</FileViewerState>
			</div>
		);
	}

	if (src && isDocx(mimeType)) {
		if (!docxHtml) {
			return (
				<div className="FileViewer is-loading">
					<div className="file-viewer-state">Loading resume...</div>
				</div>
			);
		}

		return (
			<div className="FileViewer is-docx">
				<div
					className="file-viewer-docx"
					dangerouslySetInnerHTML={{ __html: docxHtml }}
				/>
			</div>
		);
	}

	if (!src || !isPdf(mimeType)) {
		return (
			<div className="FileViewer is-unsupported">
				<FileViewerState>
					<p>
						{fileName
							? `${fileName} can’t be previewed inline.`
							: 'This file format can’t be previewed inline.'}
					</p>
					{src ? (
						<a
							className="ats-button-soft"
							href={src}
							download={fileName || 'resume'}
							target="_blank"
							rel="noreferrer">
							Download file
						</a>
					) : null}
				</FileViewerState>
			</div>
		);
	}

	return (
		<div className="FileViewer">
			<iframe
				title={fileName || 'Resume'}
				src={src}
				className="file-viewer-frame"
			/>
		</div>
	);
}
